import { useEffect, useRef, useState } from "react";

export interface UseNumericInputOptions {
  /** Current committed numeric value, owned by the parent form/business logic. */
  value: number;
  /** Called with a parsed number whenever the field holds a valid value. Never called with NaN. */
  onChange: (value: number) => void;
  /** Formats a number for display, e.g. thousand separators or "Rp 15.000". */
  format: (value: number) => string;
  /** Strips a raw input string down to the characters that matter (default: digits only). */
  parseDigits?: (raw: string) => string;
  /** If true, leaving the field empty on blur keeps it empty/invalid instead of falling back to 0. */
  required?: boolean;
  /** Value substituted when the field is left empty and is NOT required (defaults to 0). */
  emptyFallback?: number;
  max?: number;
}

/**
 * Core editing behavior shared by NumberInput and CurrencyInput.
 *
 * Key rule: while the user is typing, an empty field stays empty ("") — it is
 * never silently coerced to "0". The parent's numeric value is only updated
 * once there's a real number to report, and emptiness is only resolved
 * (to 0, or to an inline validation error) on blur.
 */
export function useNumericInput({
  value,
  onChange,
  format,
  parseDigits = (raw: string) => raw.replace(/\D/g, ""),
  required = false,
  emptyFallback = 0,
  max,
}: UseNumericInputOptions) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const isFocused = useRef(false);
  const pendingCaret = useRef<number | null>(null);

  const [displayValue, setDisplayValue] = useState<string>(() => format(value));
  const [isInvalid, setIsInvalid] = useState(false);

  // Sync from the parent's value, but only when the user isn't actively
  // editing — otherwise an unrelated re-render would stomp on their typing.
  useEffect(() => {
    if (!isFocused.current) {
      setDisplayValue(format(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Restore caret position after a formatting pass changes the string length.
  useEffect(() => {
    if (pendingCaret.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(pendingCaret.current, pendingCaret.current);
      pendingCaret.current = null;
    }
  }, [displayValue]);

  const digitsBeforeCaret = (text: string, caret: number) =>
    (text.slice(0, caret).match(/\d/g) || []).length;

  const caretAfterNDigits = (text: string, digitCount: number) => {
    if (digitCount <= 0) return 0;
    let seen = 0;
    for (let i = 0; i < text.length; i++) {
      if (/\d/.test(text[i])) {
        seen++;
        if (seen === digitCount) return i + 1;
      }
    }
    return text.length;
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    isFocused.current = true;
    setIsInvalid(false);
    e.target.select();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const caret = e.target.selectionStart ?? raw.length;
    const digitsBefore = digitsBeforeCaret(raw, caret);
    const cleaned = parseDigits(raw);

    if (cleaned === "") {
      // Requirements 1 & 2: deleting everything yields "", never "0".
      setDisplayValue("");
      return;
    }

    let num = Number(cleaned);
    if (max !== undefined) num = Math.min(num, max);

    const formatted = format(num);
    setDisplayValue(formatted);
    pendingCaret.current = caretAfterNDigits(formatted, digitsBefore);
    onChange(num);
  };

  const handleBlur = () => {
    isFocused.current = false;
    if (displayValue !== "") {
      setIsInvalid(false);
      return;
    }
    if (required) {
      // Requirement 8: required + empty triggers validation instead of a silent 0.
      setIsInvalid(true);
      return;
    }
    onChange(emptyFallback);
    setDisplayValue(format(emptyFallback));
  };

  return { displayValue, inputRef, handleChange, handleFocus, handleBlur, isInvalid };
}
